import React, { useMemo, useState } from 'react';
import {
    Activity, AlertTriangle, BarChart3, Clock, DollarSign, Download, FileSpreadsheet,
    FileText, Gauge, Info, LayoutGrid, TrendingDown, TrendingUp, Users,
} from 'lucide-react';
import {
    Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, Line, ReferenceLine,
    ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import * as XLSX from 'xlsx';

// ══════════════════════════════════════════════════════════════════════
// EVMView — Earned Value Management احترافي على مستوى المشروع كله.
//
// المبادئ اللي الصفحة دي مبنية عليها (زي ما اتفقنا):
//  • BAC/PV/EV بتتحسب من task_value الحقيقي لكل تاسك — مفيش "Weight" بيتكتب يدوي.
//  • PV بتتحسب من baseline_start/baseline_end (تاريخ مجمّد وقت إنشاء التاسك)،
//    مش من start_date/deadline_date الفعليين اللي بيتحركوا مع أي reschedule —
//    لو استخدمنا التواريخ الفعلية، الـ SPI هتفضل قريبة من 1 دايمًا حتى لو
//    المشروع متأخر جدًا، لأن "المخطط" هيبقى بيتحرك مع "الفعلي".
//  • AC حقيقي 100% من budget_expenses (مفيش تقدير).
//  • EV في منحنى الـ S-Curve بس (مش في الأرقام الحالية) بيتحسب بتقريب خطي
//    (linear interpolation) من start_date للتاسك لحد النهارده، وصولًا لنسبة
//    الإنجاز الحالية — لأننا مفيش عندنا سجل تاريخي لنسبة الإنجاز يوم بيوم.
//    ده متوضح بوضوح في الرسم عشان الشفافية مع أي جهة استشارية هتشوف التقرير.
// ══════════════════════════════════════════════════════════════════════

const fmtSAR = (n) => `SAR ${Math.round(n || 0).toLocaleString('en-US')}`;
const fmtCompact = (n) => {
    const v = Math.abs(n || 0);
    const sign = n < 0 ? '-' : '';
    if (v >= 1000000) return `${sign}${(v / 1000000).toFixed(2)}M`;
    if (v >= 1000) return `${sign}${(v / 1000).toFixed(0)}K`;
    return `${sign}${Math.round(v)}`;
};

const EVMView = ({
    darkMode, evmData, tasksData, budgetExpenses, PROJECT,
    budgetCategories = [], budgetCommitments = [], getCategorySpent, getCategoryCommitted,
    phases = [], teamData = [], milestones = [],
}) => {
    const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'manpower' | 'reports'
    const C = {
        bg: 'var(--jr-bg)',
        card: 'var(--jr-surface)',
        border: 'var(--jr-border)',
        text: 'var(--jr-text)',
        sub: 'var(--jr-text-2)',
        input: 'var(--jr-surface-2)',
    };

    const {
        bac, pv, ev, ac, spi, cpi, sv, cv, eac, eacSpiAdjusted, etc, vac,
        totalTasks, valuedTasksCount, missingValueCount, missingBaselineCount,
        contractValue,
    } = evmData || {};

    const pctComplete = bac > 0 ? (ev / bac) * 100 : 0;

    // ── S-Curve data (شهري) ──
    const sCurve = useMemo(() => {
        const today = new Date();
        const valuedTasks = (tasksData || []).filter(t => (parseFloat(t.task_value) || 0) > 0);
        const baselined = valuedTasks.filter(t => t.baseline_start && t.baseline_end);
        if (valuedTasks.length === 0) return [];

        const allDates = [];
        baselined.forEach(t => {
            const bs = new Date(t.baseline_start), be = new Date(t.baseline_end);
            if (!isNaN(bs.getTime())) allDates.push(bs);
            if (!isNaN(be.getTime())) allDates.push(be);
        });
        valuedTasks.forEach(t => { if (t.start_date) { const d = new Date(t.start_date); if (!isNaN(d.getTime())) allDates.push(d); } });
        (budgetExpenses || []).forEach(e => { if (e.expense_date) { const d = new Date(e.expense_date); if (!isNaN(d.getTime())) allDates.push(d); } });
        allDates.push(today);
        if (allDates.length === 0) return [];

        let minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
        let maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));
        minDate = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
        maxDate = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);

        const months = [];
        let cursor = new Date(minDate);
        let guard = 0;
        while (cursor <= maxDate && guard < 180) {
            months.push(new Date(cursor));
            cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
            guard++;
        }

        const plannedPctAt = (t, at) => {
            const bs = new Date(t.baseline_start), be = new Date(t.baseline_end);
            if (isNaN(bs.getTime()) || isNaN(be.getTime()) || be <= bs) return null;
            if (at <= bs) return 0;
            if (at >= be) return 100;
            return ((at - bs) / (be - bs)) * 100;
        };
        const earnedPctAt = (t, at) => {
            const cur = t.progress || 0;
            const s = t.start_date ? new Date(t.start_date) : null;
            const end = t.deadline_date ? new Date(t.deadline_date) : today;
            const cap = end < today ? end : today;
            if (!s || isNaN(s.getTime())) return at >= today ? cur : 0;
            if (at <= s) return 0;
            if (at >= cap) return cur;
            const span = cap - s;
            if (span <= 0) return at >= s ? cur : 0;
            return cur * ((at - s) / span);
        };

        return months.map(m => {
            const monthEnd = new Date(m.getFullYear(), m.getMonth() + 1, 0);
            const isFuture = monthEnd > today;

            let pvAt = 0;
            baselined.forEach(t => {
                const val = parseFloat(t.task_value) || 0;
                const pct = plannedPctAt(t, monthEnd);
                if (pct !== null) pvAt += val * (pct / 100);
            });

            let evAt = null, acAt = null;
            if (!isFuture) {
                evAt = 0;
                valuedTasks.forEach(t => {
                    const val = parseFloat(t.task_value) || 0;
                    evAt += val * (earnedPctAt(t, monthEnd) / 100);
                });
                acAt = (budgetExpenses || [])
                    .filter(e => e.expense_date && new Date(e.expense_date) <= monthEnd)
                    .reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
            }

            return {
                month: m.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }),
                PV: Math.round(pvAt),
                EV: evAt !== null ? Math.round(evAt) : null,
                AC: acAt !== null ? Math.round(acAt) : null,
                isFuture,
            };
        });
    }, [tasksData, budgetExpenses]);

    const todayLabel = new Date().toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });

    // ══════════════════════════════════════════════════════════════════
    // ✅ Manpower Histogram — نفس فكرة الـ S-Curve بالظبط (monthly buckets +
    // date overlap) بس بدل ما نحسب progress، بنحسب "متوسط عدد أعضاء الفريق
    // المتعيّنين على فازات نشطة، لكل يوم في الشهر ده". المصدر:
    // project_phases.assigned_members (مصفوفة IDs) + baseline_start/end —
    // مفيش حقل "عدد عمالة" منفصل لكل فاز في الداتابيز، فده أدق تمثيل متاح
    // فعليًا من البيانات الموجودة.
    //
    // ✅ تحسين مهم: بدل ما نحسب الشهر كله "نشط أو لأ" (binary — فاز لمدة
    // يومين بس على حدود الشهر كان بيخلي الشهر كله يظهر بعدد العمالة كامل،
    // وده بيضخّم Peak Month بشكل مُضلّل)، بنحسب الـheadcount الفريد *لكل
    // يوم* في الشهر (Set يمنع تكرار نفس العضو لو متعيّن على أكتر من فاز
    // متداخل في نفس اليوم)، وبعدين بناخد متوسط الأيام. ده بيدّي رقم
    // (ممكن يكون كسري، زي "6.4 متوسط") يعكس فعليًا كثافة التعيين خلال
    // الشهر، مش لقطة ثنائية.
    // ══════════════════════════════════════════════════════════════════
    // ✅ تصليح: لازم نفسّر baseline_start/baseline_end كـ"تاريخ محلي" مش UTC.
    // new Date('2026-09-25') (نص تاريخ بس) بيتفسّر دايمًا كمنتصف ليل UTC، لكن
    // حلقة توزيع الأيام تحت بتبني d عبر new Date(year, month, day) (منتصف
    // ليل بالتوقيت المحلي). في أي توقيت أسبق من UTC (زي السعودية UTC+3)،
    // منتصف الليل المحلي بييجي قبل لحظة الـUTC parse بـ3 ساعات، يعني
    // المقارنة p._bs <= d كانت بترجع false في أول يوم بالظبط من الـphase،
    // فيخرج الطاقم المعيّن ليها من عدّ اليوم ده بالغلط (باينة في الاختبار:
    // شهر بيبدأ فيه phase جديد كان بيطلع بعدد أقل من الصح). الحل: نفسّر
    // التاريخين بنفس الطريقة (مكوّنات محلية) زي ما بنعمل بالظبط مع d.
    const parseLocalDate = (dateStr) => {
        if (!dateStr) return null;
        const s = String(dateStr).slice(0, 10);
        const [y, mo, d] = s.split('-').map(Number);
        if (!y || !mo || !d) return null;
        return new Date(y, mo - 1, d);
    };

    const manpowerHistogram = useMemo(() => {
        const dated = (phases || []).filter(p => p.baseline_start && p.baseline_end);
        if (dated.length === 0) return [];

        const allDates = [];
        dated.forEach(p => {
            const bs = parseLocalDate(p.baseline_start), be = parseLocalDate(p.baseline_end);
            if (bs && !isNaN(bs.getTime())) allDates.push(bs);
            if (be && !isNaN(be.getTime())) allDates.push(be);
        });
        if (allDates.length === 0) return [];

        let minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
        let maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));
        minDate = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
        maxDate = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);

        const months = [];
        let cursor = new Date(minDate);
        let guard = 0;
        while (cursor <= maxDate && guard < 180) {
            months.push(new Date(cursor));
            cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
            guard++;
        }

        const memberName = (id) => teamData.find(m => String(m.id) === String(id))?.name;
        const parsedPhases = dated.map(p => ({
            ...p,
            _bs: parseLocalDate(p.baseline_start),
            _be: parseLocalDate(p.baseline_end),
        }));

        return months.map(m => {
            const monthStart = m;
            const monthEnd = new Date(m.getFullYear(), m.getMonth() + 1, 0);
            const activePhases = parsedPhases.filter(p => p._bs <= monthEnd && p._be >= monthStart);

            // متوسط الـheadcount الفريد اليومي عبر كل أيام الشهر ده
            const daysInMonth = monthEnd.getDate();
            let dailySum = 0;
            let peakDaily = 0;
            for (let day = 1; day <= daysInMonth; day++) {
                const d = new Date(m.getFullYear(), m.getMonth(), day);
                const dayMembers = new Set();
                activePhases.forEach(p => {
                    if (p._bs <= d && p._be >= d) (p.assigned_members || []).forEach(id => dayMembers.add(id));
                });
                dailySum += dayMembers.size;
                if (dayMembers.size > peakDaily) peakDaily = dayMembers.size;
            }
            const avgHeadcount = daysInMonth > 0 ? dailySum / daysInMonth : 0;

            return {
                month: m.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }),
                headcount: Math.round(avgHeadcount * 10) / 10, // رقم كسري لقرب واحد عشري
                peakHeadcount: peakDaily,
                phases: activePhases.map(p => ({
                    name: p.name,
                    system: p.system_name,
                    count: (p.assigned_members || []).length,
                    members: (p.assigned_members || []).map(memberName).filter(Boolean),
                })),
                isFuture: monthStart > new Date(),
            };
        });
    }, [phases, teamData]);

    // ✅ Peak بيتحدد بالـpeakHeadcount (أعلى عدد فريد في يوم واحد فعليًا)
    // مش بمتوسط الشهر — ده أدق تمثيل لـ"أعلى تزاحم عمالة حصل".
    const peakMonth = manpowerHistogram.reduce((best, m) => (m.peakHeadcount > (best?.peakHeadcount || 0) ? m : best), null);
    const avgHeadcountOverall = manpowerHistogram.length > 0
        ? Math.round((manpowerHistogram.reduce((s, m) => s + m.headcount, 0) / manpowerHistogram.length) * 10) / 10
        : 0;

    // ══════════════════════════════════════════════════════════════════
    // ✅ Progress Reports — تقرير أسبوعي/شهري قابل للتصدير PDF/Excel، مبني
    // على نفس بيانات evmData/tasksData/budget الموجودة فعلًا (مفيش تخزين
    // تاريخي لسنابشوتات أسبوعية — التقرير بيتولّد "as of today" مع تحديد
    // الفترة لتاسكات "اتحدثت في الفترة دي" و"متأخرة حاليًا").
    // ══════════════════════════════════════════════════════════════════
    const [reportPeriod, setReportPeriod] = useState('weekly'); // 'weekly' | 'monthly'
    const [generatingReport, setGeneratingReport] = useState(false);

    // ✅ مقارنات التاريخ هنا بتتم كـstring (YYYY-MM-DD) مش كـDate objects —
    // deadline_date/planned_date أعمدة DATE بحتة (من غير وقت)، فلو قارناها
    // بـ`new Date()` (اللي بتمثّل اللحظة الحالية بالتوقيت المحلي) كان ممكن
    // يحصل فرق ساعات حوالين منتصف الليل بتوقيت السعودية (UTC+3) يخلي تاسك
    // "متأخر" يظهر أو يختفي بالغلط. مقارنة string-to-string بين تاريخين
    // بصيغة ISO بتدّي نفس ترتيب المقارنة الزمنية الصحيح من غير أي تفسير UTC.
    const toDateStr = (d) => {
        if (!d) return null;
        if (typeof d === 'string') return d.slice(0, 10); // 'YYYY-MM-DD...' → 'YYYY-MM-DD'
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    const todayStr = toDateStr(new Date());
    const daysBetween = (dateStr, otherStr) => {
        const a = new Date(dateStr + 'T00:00:00Z').getTime();
        const b = new Date(otherStr + 'T00:00:00Z').getTime();
        return Math.round((b - a) / 86400000);
    };

    const reportRange = useMemo(() => {
        const to = new Date();
        const from = new Date();
        if (reportPeriod === 'weekly') from.setDate(from.getDate() - 7);
        else from.setDate(from.getDate() - 30);
        return { from, to };
    }, [reportPeriod]);

    const reportData = useMemo(() => {
        const { from } = reportRange;
        const all = tasksData || [];

        // ✅ progress_updated_at (لو موجود) بيتحدّث بس لما نسبة الإنجاز فعليًا
        // تتغيّر (DB trigger) — عكس updated_at اللي بيتحدّث عند أي تعديل
        // (اسم/مسؤول/ملاحظات...). لو عمود قديم لسه مش متسجل عليه قيمة، بنرجع
        // لـupdated_at كاحتياطي بدل ما نسقط التاسك من التقرير بالغلط.
        const updatedInPeriod = all.filter(t => {
            const ts = t.progress_updated_at || t.updated_at;
            return ts && new Date(ts) >= from;
        });
        const delayed = all.filter(t => {
            if (!t.deadline_date || (t.progress || 0) >= 100) return false;
            return toDateStr(t.deadline_date) < todayStr;
        }).sort((a, b) => (toDateStr(a.deadline_date) < toDateStr(b.deadline_date) ? -1 : 1));

        const upcomingMilestones = (milestones || [])
            .filter(ms => ms.planned_date && !ms.actual_date && toDateStr(ms.planned_date) >= todayStr)
            .sort((a, b) => (toDateStr(a.planned_date) < toDateStr(b.planned_date) ? -1 : 1))
            .slice(0, 8);

        const budgetRows = (budgetCategories || []).map(cat => ({
            name: cat.category_name,
            allocated: cat.allocated,
            spent: getCategorySpent ? getCategorySpent(cat.category_id) : 0,
            committed: getCategoryCommitted ? getCategoryCommitted(cat.category_id) : 0,
        }));

        return { updatedInPeriod, delayed, upcomingMilestones, budgetRows };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- todayStr/toDateStr ثابتين لمدة الـrender، مش محتاجين يبقوا dependencies
    }, [tasksData, milestones, budgetCategories, getCategorySpent, getCategoryCommitted, reportRange]);

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

    const generateProgressReportPDF = async () => {
        setGeneratingReport(true);
        try {
            const { jsPDF } = window.jspdf || await import('jspdf');
            const autoTable = (await import('jspdf-autotable')).default;

            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const margin = 15;
            let y = 20;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(16);
            doc.text(`${PROJECT?.name || PROJECT?.short_name || 'Project'} — ${reportPeriod === 'weekly' ? 'Weekly' : 'Monthly'} Progress Report`, margin, y);
            y += 7;
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(100);
            doc.text(`Period: ${fmtDate(reportRange.from)} – ${fmtDate(reportRange.to)}  •  Generated: ${fmtDate(new Date())}`, margin, y);
            doc.setTextColor(0);
            y += 10;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.text('Overall Progress', margin, y);
            y += 6;
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            const pct = evmData?.bac > 0 ? (evmData.ev / evmData.bac) * 100 : 0;
            doc.text(`${pct.toFixed(1)}% complete (by value)`, margin, y);
            doc.text(`SPI: ${evmData?.spi !== null && evmData?.spi !== undefined ? evmData.spi.toFixed(2) : '—'}`, margin + 70, y);
            doc.text(`CPI: ${evmData?.cpi !== null && evmData?.cpi !== undefined ? evmData.cpi.toFixed(2) : '—'}`, margin + 115, y);
            y += 6;
            doc.text(`BAC: ${fmtSAR(evmData?.bac)}`, margin, y);
            doc.text(`EV: ${fmtSAR(evmData?.ev)}`, margin + 70, y);
            doc.text(`AC: ${fmtSAR(evmData?.ac)}`, margin + 115, y);
            y += 10;

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.text(`Tasks with Progress Updates This Period (${reportData.updatedInPeriod.length})`, margin, y);
            y += 5;
            if (reportData.updatedInPeriod.length > 25) {
                doc.setFont('helvetica', 'italic');
                doc.setFontSize(8);
                doc.setTextColor(120);
                doc.text(`Showing first 25 of ${reportData.updatedInPeriod.length} — export Excel for the full list.`, margin, y);
                doc.setTextColor(0);
                y += 4;
            }
            autoTable(doc, {
                startY: y + 2,
                head: [['System', 'Task', 'Status', 'Progress', 'Progress Updated']],
                body: reportData.updatedInPeriod.slice(0, 25).map(t => [
                    t.system || '-', t.sub_task || 'Unnamed', t.status || '-', `${t.progress || 0}%`, fmtDate(t.progress_updated_at || t.updated_at),
                ]),
                styles: { fontSize: 8, cellPadding: 2.5 },
                headStyles: { fillColor: [14, 165, 233], textColor: [255, 255, 255], fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [248, 250, 252] },
                margin: { left: margin, right: margin },
            });
            y = doc.lastAutoTable.finalY + 10;

            if (y > 240) { doc.addPage(); y = 20; }
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.setTextColor(185, 28, 28);
            doc.text(`Delayed Tasks (${reportData.delayed.length})`, margin, y);
            doc.setTextColor(0);
            y += 5;
            if (reportData.delayed.length > 25) {
                doc.setFont('helvetica', 'italic');
                doc.setFontSize(8);
                doc.setTextColor(120);
                doc.text(`Showing first 25 of ${reportData.delayed.length} (earliest deadline first) — export Excel for the full list.`, margin, y);
                doc.setTextColor(0);
                y += 4;
            }
            autoTable(doc, {
                startY: y + 2,
                head: [['System', 'Task', 'Deadline', 'Days Overdue', 'Progress']],
                body: reportData.delayed.slice(0, 25).map(t => {
                    const days = daysBetween(toDateStr(t.deadline_date), todayStr);
                    return [t.system || '-', t.sub_task || 'Unnamed', fmtDate(t.deadline_date), days, `${t.progress || 0}%`];
                }),
                styles: { fontSize: 8, cellPadding: 2.5 },
                headStyles: { fillColor: [185, 28, 28], textColor: [255, 255, 255], fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [254, 242, 242] },
                margin: { left: margin, right: margin },
            });
            y = doc.lastAutoTable.finalY + 10;

            if (y > 230) { doc.addPage(); y = 20; }
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.setTextColor(0);
            doc.text('Budget Snapshot', margin, y);
            y += 3;
            autoTable(doc, {
                startY: y + 3,
                head: [['Category', 'Allocated', 'Spent', 'Committed', 'Available']],
                body: reportData.budgetRows.map(r => [
                    r.name, fmtSAR(r.allocated), fmtSAR(r.spent), fmtSAR(r.committed), fmtSAR(r.allocated - r.spent - r.committed),
                ]),
                styles: { fontSize: 8, cellPadding: 2.5 },
                headStyles: { fillColor: [21, 128, 61], textColor: [255, 255, 255], fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [240, 253, 244] },
                margin: { left: margin, right: margin },
            });

            doc.save(`Progress_Report_${reportPeriod}_${new Date().toISOString().split('T')[0]}.pdf`);
        } catch (err) {
            console.error('Failed to generate progress report PDF:', err);
        } finally {
            setGeneratingReport(false);
        }
    };

    const generateProgressReportExcel = () => {
        setGeneratingReport(true);
        try {
            const wb = XLSX.utils.book_new();

            const pct = evmData?.bac > 0 ? (evmData.ev / evmData.bac) * 100 : 0;
            const summarySheet = XLSX.utils.json_to_sheet([{
                'Project': PROJECT?.name || PROJECT?.short_name || '-',
                'Period': reportPeriod === 'weekly' ? 'Weekly' : 'Monthly',
                'From': fmtDate(reportRange.from),
                'To': fmtDate(reportRange.to),
                '% Complete (by value)': pct.toFixed(1),
                'BAC': evmData?.bac || 0,
                'PV': evmData?.pv || 0,
                'EV': evmData?.ev || 0,
                'AC': evmData?.ac || 0,
                'SPI': evmData?.spi !== null && evmData?.spi !== undefined ? evmData.spi.toFixed(2) : '',
                'CPI': evmData?.cpi !== null && evmData?.cpi !== undefined ? evmData.cpi.toFixed(2) : '',
                'EAC (cost-only)': evmData?.eac || '',
                'EAC (schedule-adjusted)': evmData?.eacSpiAdjusted || '',
            }]);
            XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

            const updatedSheet = XLSX.utils.json_to_sheet(reportData.updatedInPeriod.map(t => ({
                System: t.system || '-', Task: t.sub_task || 'Unnamed', Status: t.status || '-',
                'Progress %': t.progress || 0, 'Progress Updated': fmtDate(t.progress_updated_at || t.updated_at),
            })));
            XLSX.utils.book_append_sheet(wb, updatedSheet, 'Progress Updates');

            const delayedSheet = XLSX.utils.json_to_sheet(reportData.delayed.map(t => ({
                System: t.system || '-', Task: t.sub_task || 'Unnamed', Deadline: fmtDate(t.deadline_date),
                'Days Overdue': daysBetween(toDateStr(t.deadline_date), todayStr), 'Progress %': t.progress || 0,
            })));
            XLSX.utils.book_append_sheet(wb, delayedSheet, 'Delayed Tasks');

            const budgetSheet = XLSX.utils.json_to_sheet(reportData.budgetRows.map(r => ({
                Category: r.name, Allocated: r.allocated, Spent: r.spent, Committed: r.committed,
                Available: r.allocated - r.spent - r.committed,
            })));
            XLSX.utils.book_append_sheet(wb, budgetSheet, 'Budget');

            const manpowerSheet = XLSX.utils.json_to_sheet(manpowerHistogram.map(m => ({ Month: m.month, 'Avg Headcount': m.headcount, 'Peak Headcount': m.peakHeadcount })));
            XLSX.utils.book_append_sheet(wb, manpowerSheet, 'Manpower');

            XLSX.writeFile(wb, `Progress_Report_${reportPeriod}_${new Date().toISOString().split('T')[0]}.xlsx`);
        } catch (err) {
            console.error('Failed to generate progress report Excel:', err);
        } finally {
            setGeneratingReport(false);
        }
    };

    // ── Data quality ──
    const missingValueTasks = (tasksData || []).filter(t => !((parseFloat(t.task_value) || 0) > 0)).slice(0, 8);
    const missingBaselineTasks = (tasksData || []).filter(t => (parseFloat(t.task_value) || 0) > 0 && (!t.baseline_start || !t.baseline_end)).slice(0, 8);
    const valueCoveragePct = totalTasks > 0 ? (valuedTasksCount / totalTasks) * 100 : 0;
    const baselineCoveragePct = valuedTasksCount > 0 ? ((valuedTasksCount - missingBaselineCount) / valuedTasksCount) * 100 : 0;

    const perfColor = (idx, goodAt = 1) => {
        if (idx === null || idx === undefined) return C.sub;
        if (idx >= goodAt * 0.95) return '#15803D';
        if (idx >= goodAt * 0.85) return '#B45309';
        return '#B91C1C';
    };
    const varColor = (v) => (v === null || v === undefined) ? C.sub : v >= 0 ? '#15803D' : '#B91C1C';

    const StatCard = ({ label, value, sub, color, Icon }) => (
        <div style={{
            padding: '14px 16px', borderRadius: 14,
            background: C.card, border: `1px solid ${C.border}`,
            position: 'relative', overflow: 'hidden', boxShadow: 'var(--jr-shadow-sm)',
        }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: color, borderRadius: '2px 2px 0 0' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                {Icon && <Icon size={12} color={color} />}
                <span style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: C.sub }}>{label}</span>
            </div>
            <div style={{ fontSize: 20, fontWeight: 900, color, letterSpacing: '-0.02em', lineHeight: 1, marginBottom: 4 }}>{value}</div>
            {sub && <div style={{ fontSize: 9, color: C.sub, fontWeight: 600 }}>{sub}</div>}
        </div>
    );

    return (
        <div style={{ minHeight: '100vh', background: C.bg, padding: '20px 24px' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{ width: 38, height: 38, borderRadius: 11, background: '#0EA5E9', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(14,165,233,0.3)', flexShrink: 0 }}>
                    <Gauge size={19} color="#fff" />
                </div>
                <div>
                    <h1 style={{ fontSize: 20, fontWeight: 900, letterSpacing: '-0.02em', color: C.text, lineHeight: 1 }}>Earned Value Management</h1>
                    <p style={{ fontSize: 11, color: C.sub, fontWeight: 500, marginTop: 2 }}>
                        {pctComplete.toFixed(1)}% complete (by value) • {valuedTasksCount} of {totalTasks} tasks have a budgeted value
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 18, borderBottom: `1px solid ${C.border}` }}>
                {[
                    { id: 'overview', label: 'Overview', Icon: Gauge },
                    { id: 'manpower', label: 'Manpower Histogram', Icon: Users },
                    { id: 'reports', label: 'Progress Reports', Icon: FileText },
                ].map(tab => (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px',
                            background: 'transparent', border: 'none', cursor: 'pointer',
                            fontSize: 12, fontWeight: 800, color: activeTab === tab.id ? '#0EA5E9' : C.sub,
                            borderBottom: activeTab === tab.id ? '2px solid #0EA5E9' : '2px solid transparent',
                            marginBottom: -1,
                        }}>
                        <tab.Icon size={13} /> {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === 'overview' && (<>
            {/* Sanity check: BAC vs contract value — نفس منطق التحقق اللي عملناه على مستوى المشروع */}
            {contractValue > 0 && bac > 0 && Math.abs(bac - contractValue) / contractValue > 0.02 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 12, background: 'rgba(180,83,9,0.08)', border: '1px solid rgba(180,83,9,0.2)', marginBottom: 16 }}>
                    <AlertTriangle size={14} color="#B45309" />
                    <span style={{ fontSize: 11, color: '#B45309', fontWeight: 600 }}>
                        Sum of task values ({fmtSAR(bac)}) doesn't match the project's contract value ({fmtSAR(contractValue)}) — check tasks with a missing/wrong Task Value below before trusting SPI/CPI.
                    </span>
                </div>
            )}

            {/* Core EVM numbers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 10 }}>
                <StatCard label="BAC — Budget at Completion" value={fmtSAR(bac)} sub="Sum of task values" color="#1D4ED8" Icon={DollarSign} />
                <StatCard label="PV — Planned Value" value={fmtSAR(pv)} sub="As of today, per baseline" color="#6D28D9" Icon={Activity} />
                <StatCard label="EV — Earned Value" value={fmtSAR(ev)} sub="Value × actual progress" color="#0EA5E9" Icon={TrendingUp} />
                <StatCard label="AC — Actual Cost" value={fmtSAR(ac)} sub="Real recorded expenses" color="#B45309" Icon={DollarSign} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 10 }}>
                <StatCard label="SPI — Schedule Performance" value={spi !== null ? spi.toFixed(2) : '—'} sub={spi !== null ? (spi >= 1 ? 'Ahead of / on schedule' : 'Behind schedule') : 'No baseline data yet'} color={perfColor(spi)} Icon={spi !== null && spi < 1 ? TrendingDown : TrendingUp} />
                <StatCard label="CPI — Cost Performance" value={cpi !== null ? cpi.toFixed(2) : '—'} sub={cpi !== null ? (cpi >= 1 ? 'Under / on budget' : 'Over budget') : 'No expenses recorded yet'} color={perfColor(cpi)} Icon={cpi !== null && cpi < 1 ? TrendingDown : TrendingUp} />
                <StatCard label="SV — Schedule Variance" value={fmtSAR(sv)} sub="EV − PV" color={varColor(sv)} Icon={sv >= 0 ? TrendingUp : TrendingDown} />
                <StatCard label="CV — Cost Variance" value={fmtSAR(cv)} sub="EV − AC" color={varColor(cv)} Icon={cv >= 0 ? TrendingUp : TrendingDown} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 20 }}>
                <StatCard label="EAC — Estimate at Completion" value={eac !== null ? fmtSAR(eac) : '—'} sub="BAC ÷ CPI, if current cost trend holds" color="#6366F1" Icon={Activity} />
                <StatCard label="EAC — Schedule-Adjusted" value={eacSpiAdjusted !== null ? fmtSAR(eacSpiAdjusted) : '—'} sub="BAC ÷ (CPI × SPI) — more conservative if delayed" color="#9333EA" Icon={Activity} />
                <StatCard label="ETC — Estimate to Complete" value={etc !== null ? fmtSAR(etc) : '—'} sub="EAC − AC" color="#6366F1" Icon={Activity} />
                <StatCard label="VAC — Variance at Completion" value={vac !== null ? fmtSAR(vac) : '—'} sub="BAC − EAC" color={varColor(vac)} Icon={vac !== null && vac < 0 ? TrendingDown : TrendingUp} />
            </div>
            {eac !== null && eacSpiAdjusted !== null && Math.abs(eacSpiAdjusted - eac) / (eac || 1) > 0.03 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 12, background: 'rgba(147,51,234,0.06)', border: '1px solid rgba(147,51,234,0.15)', marginBottom: 20, marginTop: -10 }}>
                    <Info size={14} color="#9333EA" style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: 11, color: '#9333EA', fontWeight: 600 }}>
                        The schedule-adjusted EAC ({fmtSAR(eacSpiAdjusted)}) is {eacSpiAdjusted > eac ? 'higher' : 'lower'} than the cost-only EAC ({fmtSAR(eac)}) — the gap reflects how much the current schedule delay (SPI {spi !== null ? spi.toFixed(2) : '—'}) alone could add to the final cost if it isn't recovered.
                    </span>
                </div>
            )}

            {/* S-Curve */}
            <div style={{ borderRadius: 16, border: `1px solid ${C.border}`, background: C.card, boxShadow: 'var(--jr-shadow-sm)', padding: '18px 20px', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
                    <h3 style={{ fontSize: 13, fontWeight: 800, color: C.text }}>S-Curve — PV vs EV vs AC (cumulative, monthly)</h3>
                </div>
                <p style={{ fontSize: 10, color: C.sub, marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 5 }}>
                    <Info size={12} style={{ flexShrink: 0, marginTop: 1 }} />
                    PV is exact (from each task's frozen baseline dates). AC is exact (real recorded expenses).
                    EV before today is approximated by linear interpolation from each task's start date to today — there's no daily progress log to reconstruct it exactly, so treat past EV as indicative, not audited.
                </p>
                {sCurve.length > 0 ? (
                    <ResponsiveContainer width="100%" height={320}>
                        <AreaChart data={sCurve} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                            <defs>
                                <linearGradient id="pvGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#6D28D9" stopOpacity={0.25} />
                                    <stop offset="95%" stopColor="#6D28D9" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'} />
                            <XAxis dataKey="month" tick={{ fontSize: 10, fill: C.sub }} interval="preserveStartEnd" />
                            <YAxis tick={{ fontSize: 10, fill: C.sub }} tickFormatter={fmtCompact} width={54} />
                            <Tooltip
                                formatter={(value, name) => [value !== null ? fmtSAR(value) : 'N/A', name]}
                                contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, fontSize: 11 }}
                            />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <ReferenceLine x={todayLabel} stroke={C.sub} strokeDasharray="4 4" label={{ value: 'Today', fontSize: 9, fill: C.sub, position: 'top' }} />
                            <Area type="monotone" dataKey="PV" stroke="#6D28D9" fill="url(#pvGrad)" strokeWidth={2} dot={false} connectNulls />
                            <Line type="monotone" dataKey="EV" stroke="#0EA5E9" strokeWidth={2} dot={false} connectNulls />
                            <Line type="monotone" dataKey="AC" stroke="#B45309" strokeWidth={2} dot={false} connectNulls />
                        </AreaChart>
                    </ResponsiveContainer>
                ) : (
                    <div style={{ padding: '40px 0', textAlign: 'center', color: C.sub, fontSize: 12 }}>Not enough dated, valued tasks yet to draw a curve.</div>
                )}
            </div>

            {/* Data quality */}
            <div style={{ borderRadius: 16, border: `1px solid ${C.border}`, background: C.card, boxShadow: 'var(--jr-shadow-sm)', padding: '18px 20px' }}>
                <h3 style={{ fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 4 }}>Data Quality</h3>
                <p style={{ fontSize: 10, color: C.sub, marginBottom: 14 }}>These numbers are only as accurate as the data behind them. Fix the gaps below (from the Tasks page) for a trustworthy report.</p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                    <div style={{ padding: '12px 14px', borderRadius: 12, background: C.input, border: `1px solid ${C.border}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: C.text }}>Tasks with a Task Value</span>
                            <span style={{ fontSize: 12, fontWeight: 900, color: valueCoveragePct >= 90 ? '#15803D' : valueCoveragePct >= 50 ? '#B45309' : '#B91C1C' }}>{valueCoveragePct.toFixed(0)}%</span>
                        </div>
                        <div style={{ height: 5, background: 'var(--jr-border)', borderRadius: 99, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${valueCoveragePct}%`, background: valueCoveragePct >= 90 ? '#15803D' : valueCoveragePct >= 50 ? '#B45309' : '#B91C1C', borderRadius: 99 }} />
                        </div>
                        <div style={{ fontSize: 9, color: C.sub, marginTop: 5 }}>{missingValueCount} of {totalTasks} tasks have no value — excluded from BAC/PV/EV entirely.</div>
                    </div>
                    <div style={{ padding: '12px 14px', borderRadius: 12, background: C.input, border: `1px solid ${C.border}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: C.text }}>Valued tasks with a baseline</span>
                            <span style={{ fontSize: 12, fontWeight: 900, color: baselineCoveragePct >= 90 ? '#15803D' : baselineCoveragePct >= 50 ? '#B45309' : '#B91C1C' }}>{baselineCoveragePct.toFixed(0)}%</span>
                        </div>
                        <div style={{ height: 5, background: 'var(--jr-border)', borderRadius: 99, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${baselineCoveragePct}%`, background: baselineCoveragePct >= 90 ? '#15803D' : baselineCoveragePct >= 50 ? '#B45309' : '#B91C1C', borderRadius: 99 }} />
                        </div>
                        <div style={{ fontSize: 9, color: C.sub, marginTop: 5 }}>{missingBaselineCount} valued tasks have no start/deadline date — excluded from PV only (still count toward BAC/EV).</div>
                    </div>
                </div>

                {(missingValueTasks.length > 0 || missingBaselineTasks.length > 0) && (
                    <div style={{ display: 'grid', gridTemplateColumns: missingValueTasks.length && missingBaselineTasks.length ? '1fr 1fr' : '1fr', gap: 14 }}>
                        {missingValueTasks.length > 0 && (
                            <div>
                                <p style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#B91C1C', marginBottom: 8 }}>Missing Task Value (sample)</p>
                                {missingValueTasks.map(t => (
                                    <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0', borderBottom: `1px solid ${C.border}` }}>
                                        <AlertTriangle size={11} color="#B91C1C" style={{ flexShrink: 0 }} />
                                        <span style={{ fontSize: 11, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.sub_task || 'Unnamed'} <span style={{ color: C.sub }}>· {t.system}</span></span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {missingBaselineTasks.length > 0 && (
                            <div>
                                <p style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#B45309', marginBottom: 8 }}>Missing baseline dates (sample)</p>
                                {missingBaselineTasks.map(t => (
                                    <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0', borderBottom: `1px solid ${C.border}` }}>
                                        <AlertTriangle size={11} color="#B45309" style={{ flexShrink: 0 }} />
                                        <span style={{ fontSize: 11, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.sub_task || 'Unnamed'} <span style={{ color: C.sub }}>· {t.system}</span></span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div style={{ marginTop: 16, padding: '10px 14px', borderRadius: 10, background: 'rgba(29,78,216,0.06)', border: '1px solid rgba(29,78,216,0.15)', fontSize: 10, color: C.sub, lineHeight: 1.6 }}>
                    <strong style={{ color: C.text }}>Methodology note:</strong> baseline_start/baseline_end were backfilled once from each task's current dates when this feature was introduced — they were not captured at project kickoff, so PV before that point is an approximation, not a true historical baseline. Going forward, every new task's baseline is frozen at creation and never moves on reschedule, so PV accuracy improves over time.
                </div>
            </div>
            </>)}

            {/* ══════════════════════ MANPOWER HISTOGRAM TAB ══════════════════════ */}
            {activeTab === 'manpower' && (
                <div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 16 }}>
                        <StatCard label="Peak Day Headcount" value={peakMonth ? `${peakMonth.peakHeadcount}` : '—'} sub={peakMonth ? `${peakMonth.month} — busiest single day` : 'No dated phases yet'} color="#0EA5E9" Icon={Users} />
                        <StatCard label="Average Headcount / Month" value={`${avgHeadcountOverall}`} sub="Daily-weighted average across phased months" color="#6D28D9" Icon={BarChart3} />
                        <StatCard label="Phases with assigned members" value={`${phases.filter(p => (p.assigned_members || []).length > 0).length} / ${phases.length}`} sub="Data quality for this chart" color="#0891b2" Icon={LayoutGrid} />
                    </div>

                    <div style={{ borderRadius: 16, border: `1px solid ${C.border}`, background: C.card, boxShadow: 'var(--jr-shadow-sm)', padding: '18px 20px', marginBottom: 20 }}>
                        <h3 style={{ fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 6 }}>Manpower Histogram — assigned headcount per month</h3>
                        <p style={{ fontSize: 10, color: C.sub, marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 5 }}>
                            <Info size={12} style={{ flexShrink: 0, marginTop: 1 }} />
                            Built from each phase's assigned team members (Schedule page) and baseline dates. Each bar is the <strong>average daily unique headcount</strong> across that month (a member assigned for only 3 days near a month's edge pulls far less weight than one assigned the whole month, and is counted once per day even across overlapping phases) — not a verified daily attendance count (see Team → Attendance for that).
                        </p>
                        {manpowerHistogram.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={manpowerHistogram} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'} />
                                    <XAxis dataKey="month" tick={{ fontSize: 10, fill: C.sub }} interval="preserveStartEnd" />
                                    <YAxis tick={{ fontSize: 10, fill: C.sub }} width={34} />
                                    <Tooltip
                                        formatter={(value, name, item) => [
                                            `${value} avg (peak ${item.payload.peakHeadcount})`,
                                            'Headcount',
                                        ]}
                                        contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, fontSize: 11 }}
                                    />
                                    <ReferenceLine x={todayLabel} stroke={C.sub} strokeDasharray="4 4" label={{ value: 'Today', fontSize: 9, fill: C.sub, position: 'top' }} />
                                    <Bar dataKey="headcount" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div style={{ padding: '40px 0', textAlign: 'center', color: C.sub, fontSize: 12 }}>No phases with both baseline dates and assigned members yet — add members to phases on the Schedule page.</div>
                        )}
                    </div>

                    {/* Phase breakdown for the current month */}
                    <div style={{ borderRadius: 16, border: `1px solid ${C.border}`, background: C.card, boxShadow: 'var(--jr-shadow-sm)', padding: '18px 20px' }}>
                        <h3 style={{ fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 12 }}>Active phases this month</h3>
                        {(() => {
                            const current = manpowerHistogram.find(m => m.month === todayLabel);
                            if (!current || current.phases.length === 0) {
                                return <div style={{ padding: '20px 0', textAlign: 'center', color: C.sub, fontSize: 12 }}>No phases active this month.</div>;
                            }
                            return current.phases.map((p, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '9px 0', borderBottom: i < current.phases.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                                    <div style={{ minWidth: 0 }}>
                                        <p style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{p.name} <span style={{ color: C.sub, fontWeight: 500 }}>· {p.system}</span></p>
                                        {p.members.length > 0 && <p style={{ fontSize: 10, color: C.sub, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.members.join(', ')}</p>}
                                    </div>
                                    <span style={{ fontSize: 11, fontWeight: 900, color: '#0EA5E9', flexShrink: 0 }}>{p.count} assigned</span>
                                </div>
                            ));
                        })()}
                    </div>
                </div>
            )}

            {/* ══════════════════════ PROGRESS REPORTS TAB ══════════════════════ */}
            {activeTab === 'reports' && (
                <div>
                    <div style={{ borderRadius: 16, border: `1px solid ${C.border}`, background: C.card, boxShadow: 'var(--jr-shadow-sm)', padding: '18px 20px', marginBottom: 20 }}>
                        <h3 style={{ fontSize: 13, fontWeight: 800, color: C.text, marginBottom: 4 }}>Generate Progress Report</h3>
                        <p style={{ fontSize: 10, color: C.sub, marginBottom: 16 }}>A snapshot report as of today — overall EVM progress, tasks updated/delayed within the period, and the budget snapshot. Exportable as PDF (for sharing/printing) or Excel (for further editing).</p>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18, flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', borderRadius: 10, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                                {['weekly', 'monthly'].map(p => (
                                    <button key={p} onClick={() => setReportPeriod(p)}
                                        style={{ padding: '8px 16px', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 800, textTransform: 'capitalize', background: reportPeriod === p ? '#0EA5E9' : C.input, color: reportPeriod === p ? '#fff' : C.sub }}>
                                        {p}
                                    </button>
                                ))}
                            </div>
                            <span style={{ fontSize: 11, color: C.sub, display: 'flex', alignItems: 'center', gap: 5 }}>
                                <Clock size={12} /> {fmtDate(reportRange.from)} – {fmtDate(reportRange.to)}
                            </span>
                        </div>

                        <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={generateProgressReportPDF} disabled={generatingReport}
                                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 10, border: 'none', background: generatingReport ? '#B91C1C80' : '#B91C1C', color: '#fff', fontSize: 12, fontWeight: 800, cursor: generatingReport ? 'not-allowed' : 'pointer' }}>
                                <FileText size={14} /> {generatingReport ? 'Generating…' : 'Export PDF'}
                            </button>
                            <button onClick={generateProgressReportExcel} disabled={generatingReport}
                                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 10, border: 'none', background: generatingReport ? '#15803D80' : '#15803D', color: '#fff', fontSize: 12, fontWeight: 800, cursor: generatingReport ? 'not-allowed' : 'pointer' }}>
                                <FileSpreadsheet size={14} /> {generatingReport ? 'Generating…' : 'Export Excel'}
                            </button>
                        </div>
                    </div>

                    {/* Preview counts */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 20 }}>
                        <StatCard label="Progress updates this period" value={`${reportData.updatedInPeriod.length}`} sub={`Since ${fmtDate(reportRange.from)} · progress field only`} color="#0EA5E9" Icon={Download} />
                        <StatCard label="Currently delayed" value={`${reportData.delayed.length}`} sub="Past deadline, not yet 100%" color="#B91C1C" Icon={AlertTriangle} />
                        <StatCard label="Upcoming milestones" value={`${reportData.upcomingMilestones.length}`} sub="Not yet reached" color="#6D28D9" Icon={Activity} />
                    </div>

                    {reportData.delayed.length > 0 && (
                        <div style={{ borderRadius: 16, border: `1px solid ${C.border}`, background: C.card, boxShadow: 'var(--jr-shadow-sm)', padding: '18px 20px' }}>
                            <h3 style={{ fontSize: 13, fontWeight: 800, color: '#B91C1C', marginBottom: 12 }}>Delayed tasks (preview — top 8)</h3>
                            {reportData.delayed.slice(0, 8).map((t, i) => {
                                const days = daysBetween(toDateStr(t.deadline_date), todayStr);
                                return (
                                    <div key={t.id || i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '8px 0', borderBottom: i < 7 ? `1px solid ${C.border}` : 'none' }}>
                                        <span style={{ fontSize: 11, color: C.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.sub_task || 'Unnamed'} <span style={{ color: C.sub }}>· {t.system}</span></span>
                                        <span style={{ fontSize: 10, fontWeight: 800, color: '#B91C1C', flexShrink: 0 }}>{days}d overdue</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default EVMView;
